import { useState, useEffect } from 'react';
import axios from 'axios';
import { Zap, TrendingUp, TrendingDown, AlertTriangle, Lightbulb, CheckCircle, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RecommendationCard = ({ recommendation }) => {
  const iconMap = {
    success: CheckCircle,
    warning: AlertTriangle,
    tip: Lightbulb,
    info: Info,
  };
  
  const colorMap = {
    success: 'border-l-green-500 bg-green-500/5',
    warning: 'border-l-yellow-500 bg-yellow-500/5',
    tip: 'border-l-blue-500 bg-blue-500/5',
    info: 'border-l-purple-500 bg-purple-500/5',
  };
  
  const Icon = iconMap[recommendation.type] || Info;
  
  return (
    <Card className={`border-l-4 ${colorMap[recommendation.type]}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <Icon className="h-5 w-5 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-semibold">{recommendation.title}</h4>
            <p className="text-sm text-muted-foreground mt-1">{recommendation.message}</p>
            {recommendation.events && recommendation.events.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {recommendation.events.map((event, idx) => (
                  <span key={idx} className="px-2 py-0.5 bg-secondary/50 rounded text-xs">
                    {event}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function Optimizer() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOptimization();
  }, []);

  const fetchOptimization = async () => {
    try {
      const token = localStorage.getItem('vyrus_token');
      const response = await axios.get(`${BACKEND_URL}/api/optimizer/analyze`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load optimization data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Analyzing conventions...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No data available for optimization</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold font-heading tracking-tight flex items-center gap-3" data-testid="optimizer-title">
          <Zap className="h-8 w-8 text-primary" />
          Convention Optimizer
        </h1>
        <p className="text-muted-foreground mt-1">AI-powered insights to maximize your convention ROI</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border/50">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold">{data.summary.total_events}</p>
            <p className="text-xs text-muted-foreground">Total Events</p>
          </CardContent>
        </Card>
        <Card className="bg-green-500/10 border-green-500/30">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-400">{data.summary.profitable_events}</p>
            <p className="text-xs text-muted-foreground">Profitable Events</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold">{data.summary.average_roi.toFixed(1)}%</p>
            <p className="text-xs text-muted-foreground">Average ROI</p>
          </CardContent>
        </Card>
        <Card className={`${data.summary.total_profit >= 0 ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
          <CardContent className="p-4 text-center">
            <p className={`text-3xl font-bold ${data.summary.total_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {formatCurrency(data.summary.total_profit)}
            </p>
            <p className="text-xs text-muted-foreground">Total Profit</p>
          </CardContent>
        </Card>
      </div>

      {/* Recommendations */}
      {data.recommendations && data.recommendations.length > 0 && (
        <div>
          <h2 className="text-2xl font-semibold font-heading mb-4">Recommendations</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.recommendations.map((rec, idx) => (
              <RecommendationCard key={idx} recommendation={rec} />
            ))}
          </div>
        </div>
      )}

      {/* Expense Breakdown */}
      {data.summary.expense_breakdown && Object.keys(data.summary.expense_breakdown).length > 0 && (
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg">Expense Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(data.summary.expense_breakdown)
                .sort((a, b) => b[1] - a[1])
                .map(([category, amount]) => {
                  const total = Object.values(data.summary.expense_breakdown).reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? (amount / total * 100) : 0;
                  return (
                    <div key={category}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="capitalize">{category}</span>
                        <span>{formatCurrency(amount)} ({percentage.toFixed(1)}%)</span>
                      </div>
                      <div className="h-2 bg-secondary/30 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full transition-all duration-500"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Event Rankings */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-lg">Event Rankings by ROI</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {data.event_metrics.slice(0, 10).map((event, idx) => (
              <div 
                key={event.id} 
                className={`flex items-center justify-between p-3 rounded-lg ${
                  event.profit >= 0 ? 'bg-green-500/5' : 'bg-red-500/5'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    idx < 3 ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'
                  }`}>
                    {idx + 1}
                  </span>
                  <div>
                    <p className="font-medium">{event.name}</p>
                    <p className="text-xs text-muted-foreground">{event.location}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-bold flex items-center gap-1 ${event.roi >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {event.roi >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                    {event.roi.toFixed(1)}% ROI
                  </p>
                  <p className={`text-xs ${event.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {formatCurrency(event.profit)} profit
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Events to Return To */}
      {data.event_metrics.some(e => e.would_return) && (
        <Card className="bg-primary/5 border-primary/30">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-primary" />
              Marked for Return
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {data.event_metrics
                .filter(e => e.would_return === true)
                .map(event => (
                  <span key={event.id} className="px-3 py-1.5 bg-primary/20 text-primary rounded-full text-sm">
                    {event.name}
                  </span>
                ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
